/**
 * Editing one note in place (Kapil, 2026-09-23: "I'd just want to see
 * everything I have written… and to be able to edit each one right in
 * line") — kept out of ProfileNoteItem so it is unit-tested
 * (useNoteEdit.spec.js) rather than trusted.
 *
 * One note, one text (Kapil, 2026-09-24: "it can only be a private note or has
 * to be a highlight… it can't be both"). Editing a note shared with the
 * Network IS editing its Highlight: the server gives the Network the new words
 * and a restaurant keeps what it was sent. There is nothing else to edit.
 */
import { ref, watch } from 'vue'
import { isCardTranscription } from '@/utils/noteDisplay'

/**
 * @param {Object} props - reactive: { entry, restaurantName, editing }
 * @param {Object} deps
 * @param {Object} deps.http - axios, or a fake in tests
 * @param {Function} deps.emit - ('changed' | 'done')
 */
export function useNoteEdit(props, { http, emit }) {
  const draft = ref('')
  const saving = ref(false)
  const error = ref('')

  watch(() => props.editing, on => {
    if (!on) return
    draft.value = isCardTranscription(props.entry) ? '' : (props.entry.text || '')
    error.value = ''
  }, { immediate: true })

  async function save() {
    const text = draft.value.trim()
    if (!text || saving.value) return
    saving.value = true
    error.value = ''
    try {
      await http.put(`/profile/entries/${props.entry.entryId}`, { text })
      emit('done')
      emit('changed')
    } catch (e) {
      // The server's own sentence — e.g. a phone number in a note their Network reads.
      error.value = e?.response?.data?.message || 'Could not save that right now. Please try again.'
    } finally {
      saving.value = false
    }
  }

  return { draft, saving, error, save }
}
